import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  applyDarkModeToDocument,
  DARK_MODE_STORAGE_KEY,
  readDarkModePreference,
  THEME_STORAGE_KEY,
  writeDarkModePreference,
} from '../utils/themePreference';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [darkMode, setDarkModeState] = useState(() => readDarkModePreference());

  const setDarkMode = useCallback((enabled) => {
    const next = Boolean(enabled);
    setDarkModeState(next);
    writeDarkModePreference(next);
    applyDarkModeToDocument(next);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(!darkMode);
  }, [darkMode, setDarkMode]);

  useEffect(() => {
    applyDarkModeToDocument(darkMode);
  }, [darkMode]);

  useEffect(() => {
    const onStorage = (event) => {
      if (
        event.key === DARK_MODE_STORAGE_KEY ||
        event.key === THEME_STORAGE_KEY ||
        event.key === null
      ) {
        setDarkModeState(readDarkModePreference());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({
      darkMode,
      setDarkMode,
      toggleDarkMode,
    }),
    [darkMode, setDarkMode, toggleDarkMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
